import React from "react";

type Step = {
	title: string;
	description?: string;
	// When present, renders a small "answered/total" fraction next to the
	// title (e.g. classification steps in the NMP form). Omit for steps
	// that don't track a sub-item count.
	answered?: number;
	total?: number;
};

type Props = {
	steps: Step[];
	currentStep: number;
	onStepClick?: (index: number) => void;
	isView?: boolean;
};

export const NMPStepProgress: React.FC<Props> = ({
	steps,
	currentStep,
	onStepClick,
	isView,
}) => {
	return (
		<div className="mb-10 w-100 overflow-hidden">
			<div
				className="d-flex align-items-center justify-content-between position-relative px-2 px-md-4"
				style={{ overflowX: "auto", paddingBottom: "10px" }}
			>
				{steps.map((step, index) => {
					const stepNumber = index + 1;
					const isCompleted = stepNumber < currentStep;
					const isActive = stepNumber === currentStep;
					const isPending = stepNumber > currentStep;

					return (
						<div
							key={index}
							className="d-flex align-items-center"
							style={{
								flex: index === steps.length - 1 ? "0 0 auto" : "1 1 auto",
								cursor: "pointer",
							}}
							onClick={() => {
								if (onStepClick) {
									onStepClick(index);
								}
							}}
						>
							{/* Step Content */}
							<div
								role="button"
								tabIndex={0}
								className="d-flex align-items-center justify-content-center p-2 px-3 rounded-pill cursor-pointer"
								style={{
									minWidth: isActive ? "140px" : "auto",
									backgroundColor: isActive ? "#eef2ff" : "#f8fafc",
									border: isActive
										? "2px solid #131c5d"
										: "1px solid #cbd5e1",
									boxShadow: isActive
										? "0 4px 15px rgba(19, 28, 93, 0.1)"
										: "0 1px 3px rgba(0, 0, 0, 0.05)",
									transition: "all 0.3s ease",
									zIndex: 2,
									cursor: "pointer",
								}}
								onClick={(e) => {
									e.stopPropagation();
									if (onStepClick) {
										onStepClick(index);
									}
								}}
							>
								<div
									className="rounded-circle d-flex align-items-center justify-content-center fw-bolder shadow-sm fs-5"
									style={{
										width: 44,
										height: 44,
										backgroundColor: isCompleted
											? "#10b981"
											: isActive
												? "#131c5d"
												: "#475569",
										color: "#ffffff",
										transition: "all 0.3s ease",
										flexShrink: 0,
									}}
								>
									{isCompleted ? (
										<svg
											width="22"
											height="22"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="3"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<polyline points="20 6 9 17 4 12"></polyline>
										</svg>
									) : (
										stepNumber
									)}
								</div>

								{/* Step Title (Crystal Clear Text) */}
								<div
									className="ms-3 fw-bolder d-block"
									style={{
										color: isActive ? "#131c5d" : "#1e293b",
										fontSize: isActive ? "1.15rem" : "1.05rem",
										whiteSpace: "nowrap",
										opacity: 1,
									}}
								>
									{step.title}
									{step.total !== undefined && (
										<span
											className="ms-2 fw-bold"
											style={{
												fontSize: "0.85rem",
												color: isActive ? "#131c5d" : "#64748b",
												opacity: 0.75,
											}}
										>
											{step.answered ?? 0}/{step.total}
										</span>
									)}
								</div>
							</div>

							{/* Connecting Line (Don't show after last item) */}
							{index < steps.length - 1 && (
								<div
									className="flex-grow-1 mx-2 mx-md-4 position-relative"
									style={{ height: "2px", minWidth: "20px" }}
								>
									{/* Base Line */}
									<div
										className="position-absolute w-100 h-100"
										style={{ backgroundColor: "#e2e8f0", borderRadius: "1px" }}
									/>
									{/* Active Fill Line */}
									<div
										className="position-absolute h-100"
										style={{
											width: isCompleted ? "100%" : "0%",
											backgroundColor: "#10b981",
											borderRadius: "1px",
											transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
										}}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};
